import React from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';

/**
 * Floating Action Button — the persistent bottom-right affordance.
 *
 * Modes:
 *   • "add"   → shows a plus (default).
 *   • "close" → rotates the same plus 45°, producing an X. Keeping it
 *     the *same* icon (just rotated) means the morph is a single
 *     spring rotation instead of a swap/fade — which is what the
 *     FloatingActionMenu pattern in the reference code does too,
 *     and it reads as a single, continuous object to the eye.
 *
 * Kept in the App shell (not the page) so it stays mounted across
 * route changes and the morph animates between contexts (e.g. Home
 * → Upload) instead of re-mounting into a new element.
 */
export default function FAB({
  onClick,
  ariaLabel = 'Add',
  mode = 'add', // "add" | "close"
}) {
  const isClose = mode === 'close';

  return (
    <motion.button
      onClick={onClick}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 20, delay: 0.05 }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.9 }}
      aria-label={isClose ? 'Close' : ariaLabel}
      className="fixed z-50 focus:outline-none"
      style={{
        bottom: 30,
        right: 24,
        width: 60,
        height: 60,
        borderRadius: 999,
        background: '#44231E',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        cursor: 'pointer',
        boxShadow:
          '0 4px 10px rgba(64, 42, 20, 0.18), ' +
          '0 12px 28px rgba(64, 42, 20, 0.28)',
      }}
    >
      <motion.span
        aria-hidden
        animate={{ rotate: isClose ? 45 : 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 22 }}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Plus size={32} color="#FFFFFF" strokeWidth={2.5} />
      </motion.span>
    </motion.button>
  );
}
